import { useState, useCallback, useRef, useEffect } from "react";
import { ScrollView } from "react-native";
import { View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { WebLayout, useTenantSwitcherControl } from "./WebLayout.web";
import { SettingsSection } from "./SettingsSection.web";
import { SettingsListItem } from "./SettingsListItem.web";
import { useRouter } from "expo-router";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import { useTenant, getTenantDisplayName, useIsSupportUser, useAuth } from "@src/features/tenants";
import { FEATURE_FLAGS, getBooleanFlag, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { ImpersonationSetting } from "../settings/ImpersonationSetting";
import { useIsTenantAdmin } from "@visioni/a3s-access/app";
import { useEnvironment } from "@src/config/environment-store";
import { hasStorybookEmailDomain, isStorybookPublished } from "@src/features/storybook/storybook-session";

/**
 * Settings page with web layout.
 * Organisation switcher is managed globally by WebLayout.
 */
export function SettingsWebLayout() {
  const router = useRouter();
  const { selectedTenant } = useTenant();
  const tenantSwitcher = useTenantSwitcherControl();
  const { contactUrl, termsUrl, privacyUrl, supportPhone, supportPhoneDisplay } = useRegionConfig();
  const isSupportUser = useIsSupportUser();
  const environment = useEnvironment();
  const { user } = useAuth();

  // `useBooleanFlag` rather than `getBooleanFlag`: the latter reads the flag
  // store imperatively, so React Compiler sees no reactive input and caches the
  // result against the environment and email alone. Those never change, so a
  // render that happens before the Amplitude fetch lands — the store hydrates
  // from cache first — freezes this to false for the life of the screen.
  const storybookEnabled = useBooleanFlag(FEATURE_FLAGS.STORYBOOK);

  const storybookVisible =
    isStorybookPublished(environment.key) && storybookEnabled && hasStorybookEmailDomain(user?.email);

  const isUsersAndPermissionsOptionEnabled = getBooleanFlag(FEATURE_FLAGS.USERS_PERMISSIONS_OPTION);
  const isNotificationSettingsEnabled = useBooleanFlag(FEATURE_FLAGS.NOTIFICATION_SETTINGS);
  const { isTenantAdmin } = useIsTenantAdmin();

  const handleOpenURL = useCallback(async (url: string) => {
    window.open(url, "_blank");
  }, []);

  const [copiedPhone, setCopiedPhone] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const handleCopyPhone = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(supportPhone);
      setCopiedPhone(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedPhone(false), 2000);
    } catch {
      // Fallback: the phone number is displayed as subtitle, user can copy manually
    }
  }, [supportPhone]);

  const settingsContent = (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: 40,
        paddingBottom: 40,
        paddingHorizontal: 80,
        maxWidth: sizes.webContentMaxWidth,
      }}
      showsVerticalScrollIndicator={false}
    >
      <View gap="6xl">
        {/* General Section — hidden when no items are visible */}
        {(tenantSwitcher || (isUsersAndPermissionsOptionEnabled && isTenantAdmin) || isNotificationSettingsEnabled) && (
          <SettingsSection title="General">
            {tenantSwitcher && (
              <SettingsListItem
                title="Organisation"
                subtitle={selectedTenant ? getTenantDisplayName(selectedTenant) : "Not selected"}
                onPress={() => tenantSwitcher.open()}
              />
            )}
            {isUsersAndPermissionsOptionEnabled && isTenantAdmin && (
              <SettingsListItem
                title="Users & Permissions"
                onPress={() => router.push("/settings/users-and-permissions")}
              />
            )}
            {isNotificationSettingsEnabled && (
              <SettingsListItem
                title="Notification Preferences"
                onPress={() => router.push("/settings/notification-preferences")}
              />
            )}
          </SettingsSection>
        )}

        {/* Contact Section */}
        <SettingsSection title="Contact">
          <SettingsListItem
            title={copiedPhone ? "Copied!" : "Support"}
            subtitle={supportPhoneDisplay}
            onPress={handleCopyPhone}
            showChevron={false}
          />
          <SettingsListItem title="Get a quote" onPress={() => handleOpenURL(contactUrl)} />
          <SettingsListItem title="Contact us" onPress={() => handleOpenURL(contactUrl)} />
        </SettingsSection>

        {/* Legal Section */}
        <SettingsSection title="Legal">
          <SettingsListItem title="Terms & Conditions" onPress={() => handleOpenURL(termsUrl)} />
          <SettingsListItem title="Privacy" onPress={() => handleOpenURL(privacyUrl)} />
        </SettingsSection>

        {/* Support Section — only visible to a3s:support users */}
        {isSupportUser && (
          <SettingsSection title="Support Tools">
            <ImpersonationSetting />
          </SettingsSection>
        )}

        {/* Storybook is open to any Visioni account the flag is on for, which is
            a wider audience than Support Tools above. */}
        {storybookVisible && (
          <SettingsSection title="Internal">
            <SettingsListItem
              title="Storybook"
              subtitle="Component libraries built from main"
              onPress={() => router.push("/storybook")}
            />
          </SettingsSection>
        )}
      </View>
    </ScrollView>
  );

  return <WebLayout>{settingsContent}</WebLayout>;
}
